import { useCallback, useEffect, useState } from "react";

export type FollowUpStatus = "none" | "needs" | "followed_up";

export interface ConversationAnnotations {
  tags: string[];
  followUpStatus: FollowUpStatus;
  followUpReason: string | null;
  followedUpAt: string | null;
  notes: ConversationNote[];
}

export interface ConversationNote {
  id: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

const EMPTY: ConversationAnnotations = {
  tags: [],
  followUpStatus: "none",
  followUpReason: null,
  followedUpAt: null,
  notes: [],
};

const storageKey = (conversationId: string) => `hg.conv.annotations.${conversationId}`;

/**
 * Owner-side annotations on a conversation: tags, follow-up status,
 * private notes. Backed by localStorage in phase 1 so the UI ships
 * immediately without waiting on the backend migration.
 *
 * Phase 2: swap the read/write internals for a Supabase RPC against the
 * `conversation_annotations` table (migration `20260523000001_*.sql`).
 * The hook signature stays the same so consumers don't need to change.
 *
 * Per-browser, per-user. Not shared across devices until the DB lands.
 */
export function useConversationAnnotations(conversationId: string | null) {
  const [annotations, setAnnotations] = useState<ConversationAnnotations>(EMPTY);

  // Hydrate from localStorage.
  useEffect(() => {
    if (!conversationId) {
      setAnnotations(EMPTY);
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey(conversationId));
      if (raw) {
        const parsed = JSON.parse(raw);
        setAnnotations({ ...EMPTY, ...parsed });
      } else {
        setAnnotations(EMPTY);
      }
    } catch {
      setAnnotations(EMPTY);
    }
  }, [conversationId]);

  const persist = useCallback(
    (next: ConversationAnnotations) => {
      if (!conversationId) return;
      try {
        window.localStorage.setItem(storageKey(conversationId), JSON.stringify(next));
      } catch {
        // localStorage full or unavailable; surface a toast at the call site.
      }
      setAnnotations(next);
    },
    [conversationId],
  );

  const addTag = useCallback(
    (tag: string) => {
      const clean = tag.trim();
      if (!clean) return;
      if (annotations.tags.includes(clean)) return;
      persist({ ...annotations, tags: [...annotations.tags, clean] });
    },
    [annotations, persist],
  );

  const removeTag = useCallback(
    (tag: string) => {
      persist({ ...annotations, tags: annotations.tags.filter((t) => t !== tag) });
    },
    [annotations, persist],
  );

  const setFollowUp = useCallback(
    (status: FollowUpStatus, reason?: string | null) => {
      persist({
        ...annotations,
        followUpStatus: status,
        followUpReason:
          status === "needs" ? (reason ?? annotations.followUpReason ?? null) : null,
        followedUpAt: status === "followed_up" ? new Date().toISOString() : null,
      });
    },
    [annotations, persist],
  );

  const addNote = useCallback(
    (body: string) => {
      const clean = body.trim();
      if (!clean) return;
      const now = new Date().toISOString();
      const note: ConversationNote = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        body: clean,
        createdAt: now,
        updatedAt: now,
      };
      persist({ ...annotations, notes: [note, ...annotations.notes] });
    },
    [annotations, persist],
  );

  const updateNote = useCallback(
    (id: string, body: string) => {
      const clean = body.trim();
      const now = new Date().toISOString();
      persist({
        ...annotations,
        notes: annotations.notes.map((n) =>
          n.id === id ? { ...n, body: clean, updatedAt: now } : n,
        ),
      });
    },
    [annotations, persist],
  );

  const deleteNote = useCallback(
    (id: string) => {
      persist({ ...annotations, notes: annotations.notes.filter((n) => n.id !== id) });
    },
    [annotations, persist],
  );

  return {
    annotations,
    addTag,
    removeTag,
    setFollowUp,
    addNote,
    updateNote,
    deleteNote,
  };
}

/** Predefined tags shown as quick-pick chips. Customers can add free-text too. */
export const DEFAULT_TAGS = [
  "Priority",
  "Complaint",
  "Good lead",
  "Follow up",
  "Showcase",
] as const;
